import { useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import moment from "moment";
import { toast } from "react-hot-toast";
import { CardComp, ButtonComp } from "..";
import InvestorStatusBadge from "./InvestorStatusBadge";
import { formatMoney } from "../../utils/currencyFormat";
import { convertToTitleCase } from "../../utils/helpers";
import { useCancelRealEstateRequestMutation } from "../../store/investor/investorRealEstateApi";
import ConfirmationModal from "../Common/ConfirmationModal";
import UnifiedTimeline from "../shared/Timeline/UnifiedTimeline";

const PageGrid = styled.div`
  display: grid;
  gap: 1.25rem;

  @media (min-width: 992px) {
    grid-template-columns: minmax(0, 1fr) minmax(280px, 320px);
    gap: 1.5rem 2rem;
    align-items: start;
  }
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.85rem 0;
  border-bottom: 1px solid #f1f1f1;
  font-size: 0.9rem;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  .label {
    color: #525252;
    flex-shrink: 0;
  }

  .value {
    color: #383838;
    font-weight: 600;
    text-align: right;
    word-break: break-word;
  }
`;

export default function RealEstateRequestDetailContent({ request }) {
  const navigate = useNavigate();
  const [showCancel, setShowCancel] = useState(false);
  const [cancelRequest, { isLoading }] = useCancelRealEstateRequestMutation();

  if (!request) return null;

  const re = request.realEstate || {};
  const inspection = request.inspection;
  const timeline = request.timeline || [];

  const handleCancel = async () => {
    try {
      await cancelRequest({
        requestReference: request.reference,
        reason: "Cancelled by customer",
      }).unwrap();
      toast.success("Request cancelled");
      setShowCancel(false);
      navigate("/invest/requests?filter=real_estate");
    } catch (error) {
      toast.error(error?.data?.message || "Unable to cancel request.");
    }
  };

  return (
    <>
      <PageGrid>
        <div>
          <CardComp cardClassName="border-0 shadow-sm rounded-4 mb-3">
            <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
              <InvestorStatusBadge
                status="real_estate"
                label="Property request"
                backgroundColor="var(--color-secondary)"
              />
              <InvestorStatusBadge status={request.status || "submitted"} />
            </div>
            <h1 className="h5 fw-bold mb-2" style={{ color: "#383838" }}>
              {re.propertyName || request.title}
            </h1>
            <p className="text-muted small mb-0">
              Reference <strong>{request.reference}</strong>
              {request.submittedAt
                ? ` · ${moment(request.submittedAt).format("DD MMM YYYY, HH:mm")}`
                : ""}
            </p>
          </CardComp>

          <CardComp cardClassName="border-0 shadow-sm rounded-4 mb-3">
            <h2 className="h6 fw-bold mb-3" style={{ color: "#383838" }}>
              Property details
            </h2>
            <DetailRow>
              <span className="label">Location</span>
              <span className="value">{re.location || "—"}</span>
            </DetailRow>
            <DetailRow>
              <span className="label">Units / plots</span>
              <span className="value">{re.unitsRequested ?? "—"}</span>
            </DetailRow>
            <DetailRow>
              <span className="label">Estimated value</span>
              <span className="value">
                {request.estimatedValueNgn != null ? formatMoney(request.estimatedValueNgn) : "—"}
              </span>
            </DetailRow>
            <DetailRow>
              <span className="label">Inspection preference</span>
              <span className="value">
                {re.inspectionPreference ? convertToTitleCase(re.inspectionPreference) : "—"}
              </span>
            </DetailRow>
            <DetailRow>
              <span className="label">Documentation interest</span>
              <span className="value">{re.documentationInterest ? "Yes" : "No"}</span>
            </DetailRow>
            <DetailRow>
              <span className="label">Preferred contact</span>
              <span className="value">
                {request.preferredContact ? convertToTitleCase(request.preferredContact) : "—"}
              </span>
            </DetailRow>
            {request.customerNote ? (
              <DetailRow>
                <span className="label">Your note</span>
                <span className="value">{request.customerNote}</span>
              </DetailRow>
            ) : null}
          </CardComp>

          {inspection ? (
            <CardComp cardClassName="border-0 shadow-sm rounded-4 mb-3">
              <h2 className="h6 fw-bold mb-3" style={{ color: "#383838" }}>
                Inspection
              </h2>
              <DetailRow>
                <span className="label">Status</span>
                <span className="value">{convertToTitleCase(inspection.status || "—")}</span>
              </DetailRow>
              <DetailRow>
                <span className="label">Date</span>
                <span className="value">
                  {inspection.date ? moment(inspection.date).format("DD MMM YYYY") : "—"}
                  {inspection.time ? ` · ${inspection.time}` : ""}
                </span>
              </DetailRow>
              <DetailRow>
                <span className="label">Location</span>
                <span className="value">{inspection.location || "—"}</span>
              </DetailRow>
              {inspection.assignedStaffName ? (
                <DetailRow>
                  <span className="label">Staff contact</span>
                  <span className="value">{inspection.assignedStaffName}</span>
                </DetailRow>
              ) : null}
            </CardComp>
          ) : null}

          <CardComp cardClassName="border-0 shadow-sm rounded-4 mb-3">
            <h2 className="h6 fw-bold mb-3" style={{ color: "#383838" }}>
              Timeline
            </h2>
            <UnifiedTimeline
              items={timeline}
              showActor={false}
              emptyMessage="No timeline events yet."
            />
          </CardComp>
        </div>

        <div>
          <CardComp cardClassName="border-0 shadow-sm rounded-4 mb-3">
            <h2 className="h6 fw-bold mb-2" style={{ color: "#383838" }}>
              Next step
            </h2>
            <p className="small mb-3" style={{ color: "#525252" }}>
              {request.nextStep}
            </p>
            {request.canCancel ? (
              <ButtonComp
                btnText="Cancel request"
                type="button"
                btnClassName="btn w-100"
                ButtonInlineStyle={{
                  background: "#fff",
                  border: "1px solid #FF4343",
                  color: "#FF4343",
                }}
                onClick={() => setShowCancel(true)}
              />
            ) : null}
          </CardComp>
        </div>
      </PageGrid>

      <ConfirmationModal
        isOpen={showCancel}
        onClose={() => setShowCancel(false)}
        title="Cancel property request?"
        message="This will cancel your property request. You can submit a new request later."
        confirmText="Cancel request"
        onConfirm={handleCancel}
        isLoading={isLoading}
      />
    </>
  );
}
